import { isFlutterInAppWebView } from "./webView";

export const MOBILE_APP_IOS_STORE_URL = "https://apps.apple.com/in/app/bt-caregiver/id6482351707";
export const MOBILE_APP_ANDROID_STORE_URL = "https://play.google.com/store/apps/details?id=com.e2infosystems.bt_caregiver&pcampaignid=web_share";
export const MOBILE_APP_ANDROID_PACKAGE = "com.e2infosystems.bt_caregiver";
export const MOBILE_APP_DEEP_LINK_SCHEME = "btcaregiver";

const APP_OPEN_DETECT_MS = 2200;
const NATIVE_OPEN_APP_HANDLERS = ["openApp", "openMobileApp", "launchApp"];

export const detectMobileOs = () => {
  const userAgent = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/i.test(userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isIos) {
    return "ios";
  }

  if (/Android/i.test(userAgent)) {
    return "android";
  }

  return "other";
};

export const isIosSafariBrowser = () => detectMobileOs() === "ios" && !isFlutterInAppWebView();

export const getStoreUrlForOs = (os = detectMobileOs()) => {
  if (os === "ios") {
    return MOBILE_APP_IOS_STORE_URL;
  }
  if (os === "android") {
    return MOBILE_APP_ANDROID_STORE_URL;
  }
  return null;
};

/** Opens the App Store or Play Store page for the current device. */
export const openMobileAppStore = () => {
  const storeUrl = getStoreUrlForOs() || MOBILE_APP_IOS_STORE_URL;
  window.location.assign(storeUrl);
};

const buildAppDeepLink = (os) => {
  if (os === "android") {
    const fallback = encodeURIComponent(MOBILE_APP_ANDROID_STORE_URL);
    return `intent://login#Intent;scheme=${MOBILE_APP_DEEP_LINK_SCHEME};package=${MOBILE_APP_ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
  }

  if (os === "ios") {
    return `${MOBILE_APP_DEEP_LINK_SCHEME}://login`;
  }

  return null;
};

const openStoreUrl = (url) => {
  window.location.assign(url);
};

const tryNativeOpenApp = async () => {
  if (!isFlutterInAppWebView()) {
    return false;
  }

  for (const handlerName of NATIVE_OPEN_APP_HANDLERS) {
    try {
      await window.flutter_inappwebview.callHandler(handlerName);
      return true;
    } catch {
      // Try the next handler name supported by the host app.
    }
  }

  return false;
};

const watchForAppLaunch = (onStillVisible) => {
  let appOpened = false;

  const markAppOpened = () => {
    appOpened = true;
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      markAppOpened();
    }
  };

  const handlePageHide = () => {
    markAppOpened();
  };

  const handleBlur = () => {
    markAppOpened();
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pagehide", handlePageHide);
  window.addEventListener("blur", handleBlur);

  window.setTimeout(() => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", handlePageHide);
    window.removeEventListener("blur", handleBlur);

    if (!appOpened && !document.hidden) {
      onStillVisible();
    }
  }, APP_OPEN_DETECT_MS);
};

const launchHiddenDeepLink = (deepLink) => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.setAttribute("aria-hidden", "true");
  iframe.src = deepLink;
  document.body.appendChild(iframe);

  window.setTimeout(() => {
    iframe.remove();
  }, APP_OPEN_DETECT_MS);
};

const launchDeepLinkFromUserGesture = (deepLink) => {
  const link = document.createElement("a");
  link.href = deepLink;
  link.style.display = "none";
  link.setAttribute("aria-hidden", "true");
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/**
 * iOS Safari: try custom-scheme deep link first (opens installed app), then App Store.
 * Uses a hidden iframe + anchor click while the user gesture is active.
 */
const openIosSafariApp = ({ onAppNotInstalled }) => {
  const deepLink = buildAppDeepLink("ios");
  if (!deepLink) {
    onAppNotInstalled?.("ios");
    return;
  }

  watchForAppLaunch(() => {
    openStoreUrl(MOBILE_APP_IOS_STORE_URL);
    onAppNotInstalled?.("ios");
  });

  launchHiddenDeepLink(deepLink);
  launchDeepLinkFromUserGesture(deepLink);
};

const openWithDeepLink = ({ os, onAppNotInstalled }) => {
  const deepLink = buildAppDeepLink(os);

  if (!deepLink) {
    onAppNotInstalled?.(os);
    return;
  }

  watchForAppLaunch(() => {
    onAppNotInstalled?.(os);
  });

  window.location.href = deepLink;
};

/**
 * Attempts to open the native app. Calls onAppNotInstalled when the page stays
 * visible (app likely not installed or deep link unavailable).
 */
export const openMobileApp = async ({ onAppNotInstalled } = {}) => {
  const os = detectMobileOs();

  if (os === "ios") {
    if (isIosSafariBrowser()) {
      openIosSafariApp({ onAppNotInstalled });
      return;
    }

    if (await tryNativeOpenApp()) {
      return;
    }
  }

  openWithDeepLink({ os, onAppNotInstalled });
};
